import clientApi from '@/api/client';
import posApi from '@/api/pos';
import { ClientProps } from '@/interfaces/models/client';
import {ReviewProps} from '@/interfaces/models/review';
import Client from '@/models/client';
import Review from '@/models/review';
import { RootState } from '@/store';
import { ActionContext, ActionTree, GetterTree, MutationTree } from 'vuex';

const namespaced: boolean = true;

type ClientsState = {
  sectionNotification: number;
  clients: any[];
  isFetchingClients: boolean;
  isClientsWasFetch: boolean;
};

const getDefaultState = (): ClientsState => {
  return {
    sectionNotification: 0,
    clients: [],
    isFetchingClients: false,
    isClientsWasFetch: false
  };
};

const state: ClientsState = getDefaultState();

const getters: GetterTree<ClientsState, RootState> = {
  clientsGroupedByFirstLetterOfName(state) {
    return state.clients.reduce((groups: any, client: Client) => {
      if (!groups.hasOwnProperty(client.firstLetterOfName)) {
        groups[client.firstLetterOfName] = [];
      }

      groups[client.firstLetterOfName].push(client);

      return groups;
    }, {});
  }
};

const actions: ActionTree<ClientsState, RootState> = {
  async fetchClients({ commit, state }: ActionContext<ClientsState, RootState>) {
    if (!state.isClientsWasFetch && !state.isFetchingClients) {
      commit('setIsFetchingClients', true);

      return clientApi.fetchClients()
        .then((clients: any) => {
          commit('setClients', clients);
          commit('setIsFetchingClients', false);
          commit('setIsClientsWasFetch', true);
        });
    }
  },
  async createClient({ dispatch }: ActionContext<ClientsState, RootState>, clientProps: any) {
    const client: any = await clientApi.createClient(clientProps);

    dispatch('socketCreateClient', client, { root: true });

    return client;
  },
  async createClientFromPos({ dispatch }: ActionContext<ClientsState, RootState>, clientProps: any) {
    const client: any = await posApi.createClient(clientProps);

    dispatch('socketCreateClient', client, { root: true });

    return client;
  },
  async editClient({ dispatch }: ActionContext<ClientsState, RootState>, payload: any) {
    clientApi.editClient(payload);

    return dispatch(
      'socketEditClient',
      {
        id: payload.id,
        ...payload.editedFields
      },
      {
        root: true
      });
  },
  async removeClient({ dispatch }: ActionContext<ClientsState, RootState>, clientId: string) {
    clientApi.removeClient(clientId);

    return dispatch('socketRemoveClient', { id: clientId }, { root: true });
  },
  async makeClientNormal({ dispatch }: ActionContext<ClientsState, RootState>, clientId: string) {
    clientApi.makeClientNormal(clientId);

    return dispatch('socketNormalClient', { id: clientId }, { root: true });
  },
  async makeClientBanned({ dispatch }: ActionContext<ClientsState, RootState>, clientId: string) {
    clientApi.makeClientBanned(clientId);

    return dispatch('socketBannedClient', { id: clientId }, { root: true });
  },
  async makeClientFavorite({ dispatch }: ActionContext<ClientsState, RootState>, clientId: string) {
    clientApi.makeClientFavorite(clientId);

    return dispatch('socketFavoriteClient', { id: clientId }, { root: true });
  },
  async createMessage({ dispatch }: ActionContext<ClientsState, RootState>, payload: any) {
    const messageProps = await clientApi.createMessage(payload);

    return dispatch('socketCreateMessage', messageProps, { root: true });
  }
};

const mutations: MutationTree<ClientsState> = {
  resetState(state) {
    Object.assign(state, getDefaultState());
  },
  setSectionNotification(state, sectionNotification: any) {
    state.sectionNotification = sectionNotification;
  },
  decreaseSectionNotification(state, sectionNotificationCount: number) {
    if (state.sectionNotification <= sectionNotificationCount) {
      state.sectionNotification = 0;

      return;
    }

    state.sectionNotification -= sectionNotificationCount;
  },
  setClients(state, clients: any[]) {
    state.clients = clients;
  },
  addClient(state, client: Client) {
    state.clients.push(client);
  },
  editClient(state, clientProps: ClientProps) {
    const client = state.clients.find(({ id }: Client) => id === clientProps.id);

    if (client !== undefined) {
      client.mergeWithNewFields(clientProps);
    }
  },
  removeClient(state, clientId: string) {
    const clientIndex = state.clients.findIndex(({ id }: Client) => id === clientId);

    if (clientIndex !== -1) {
      state.clients.splice(clientIndex, 1);
    }
  },
  makeClientNormal(state, clientId: string) {
    const client: Client | undefined = state.clients.find(({ id }: Client) => id === clientId);

    if (client !== undefined) {
      client.makeNormal();
    }
  },
  makeClientFavorite(state, clientId: string) {
    const client: Client | undefined = state.clients.find(({ id }: Client) => id === clientId);

    if (client !== undefined) {
      client.makeFavorite();
    }
  },
  makeClientBanned(state, clientId: string) {
    const client: Client | undefined = state.clients.find(({ id }: Client) => id === clientId);

    if (client !== undefined) {
      client.makeBanned();
    }
  },
  createMessage(state, messageProps) {

  },
  createReview(state, { id, review }: { id: string, review: ReviewProps }) {
    const client: Client | undefined = state.clients.find((client: Client) => client.id === id);

    if (client !== undefined) {
      client.unreadReviews.push(new Review(review));
    }
  },
  setIsFetchingClients(state, isFetchingClients) {
    state.isFetchingClients = isFetchingClients;
  },
  setIsClientsWasFetch(state, isClientsWasFetch) {
    state.isClientsWasFetch = isClientsWasFetch;
  }
};

export default {
  namespaced,
  state,
  getters,
  actions,
  mutations
};
